/**
 * What: Shared TestFeed credit activity statement query for Shopify app surfaces.
 * Transport / data-flow: Calls the Shopify Worker `/api/testfeed-usage/activity` proxy.
 */

import { useQuery } from '@tanstack/react-query'
import { shopifyApiFetch, type ShopifyAppBridge } from '../shopifyApiFetch.js'
import { shopifyQueryKey, useShopifyAppContext } from '../ShopifyAppContext.js'
import { SHOPIFY_CREDIT_RECEIPTS_CAPABILITY, type ShopifyCreditActivityPage } from '@testfeed/shopify-contracts'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'

async function loadTestFeedUsageActivity(
  shopify: ShopifyAppBridge,
  cursor?: string | null,
  limit?: number,
): Promise<ShopifyCreditActivityPage> {
  const query = new URLSearchParams({ capability: SHOPIFY_CREDIT_RECEIPTS_CAPABILITY })
  if (limit) query.set('limit', String(limit))
  if (cursor) query.set('cursor', cursor)
  const queryString = query.toString()
  const path = `/api/testfeed-usage/activity${queryString ? `?${queryString}` : ''}`

  const response = await shopifyApiFetch(shopify, path)
  if (response.ok) return response.json()

  const payload = (await response.json().catch(() => ({}))) as { error?: unknown }
  const message =
    typeof payload.error === 'string' ? payload.error : `Activity statement request failed (${response.status})`
  throw new Error(message)
}

export function testFeedUsageActivityQueryKey(
  shopDomain: string,
  orgId: string | null,
  connectionId: string | null,
  cursor?: string | null,
  limit?: number,
) {
  return shopifyQueryKey(shopDomain, orgId, connectionId, [
    'testfeed-usage-activity',
    cursor ?? 'first',
    String(limit ?? 20),
  ] as const)
}

export function useTestFeedUsageActivity(
  shopify: ShopifyAppBridge,
  options: { cursor?: string | null; limit?: number; enabled?: boolean } = {},
) {
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  return useQuery({
    queryKey: testFeedUsageActivityQueryKey(shopDomain, orgId, connectionId, options.cursor, options.limit),
    queryFn: () => loadTestFeedUsageActivity(shopify, options.cursor, options.limit),
    ...shopifyQueryPolicies.usageActivity,
    enabled: (options.enabled ?? true) && !!shopDomain && !!orgId && !!connectionId,
  })
}
